import forEach from 'lodash/forEach';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import { trimSearch } from '@xmatters/common/utils/searchUtil';
import { encodeReduxFieldName } from '@xmatters/common/utils/string';
import { getListingURIGenerator } from '@xmatters/redux/actions/listPage/utils';

const uri = '/webapi/xm/1/people';

export const SEARCH_USERS_LIST_PAGE = 'SEARCH_USER';
export const SEARCH_USERS_LIST_PAGE_START = `${SEARCH_USERS_LIST_PAGE}_START`;
export const SEARCH_USERS_LIST_PAGE_SUCCESS = `${SEARCH_USERS_LIST_PAGE}_SUCCESS`;
export const SEARCH_USERS_LIST_PAGE_FAIL = `${SEARCH_USERS_LIST_PAGE}_FAIL`;

// Some items in the same filter dropdown should instead be sent to the API under devicesStatus instead of deviceTestStatus:
const DEVICE_STATUS_KEYS = new Set(['INACTIVE']);

export const splitDeviceTypesForApi = props => {
  return groupBy(get(props, 'devicesTestStatus.value'), item =>
    DEVICE_STATUS_KEYS.has(item) ? 'devicesStatus' : 'devicesTestStatus',
  );
};

const filterIfHasValue = filter => (isEmpty(filter) ? undefined : filter);

const getSupervisorsValue = supervisors => {
  if (supervisors && supervisors.length === 2) {
    const [supervisorsId, supervisorsExists] = supervisors;
    return {
      supervisorsId,
      supervisorsExists,
    };
  }
  if (supervisors[0] === 'false') {
    return { supervisorsExists: supervisors[0] };
  }
  return {
    supervisorsId: supervisors[0],
  };
};

const getSitesValue = sites => sites.map(({ value }) => value);

const getGroupsValue = groups => {
  const groupNames = [];
  const result = { groupNames };
  groups.forEach(group => {
    const groupValue = group.value || group;
    if (groupValue === 'USER_GROUPS_FILTER_NO_MEMBERSHIP') {
      result.groupExists = 'false';
    } else {
      groupNames.push(groupValue);
    }
  });

  return result;
};

const deserializeCustomProps = props => {
  const matchCustomProp = prop => prop.match(/^CustomProperty\.(.+)/);

  const customProperties = {};
  const results = { customProperties };

  forEach(props, (value, key) => {
    const match = matchCustomProp(key);
    if (match) {
      customProperties[match[1]] = value;
    } else {
      results[key] = value;
    }
  });

  return results;
};

export const getSearchFilterParams = props => {
  const search = get(props, 'search');

  const devicesExists = get(props, 'devicesExists.value');
  const { devicesStatus, devicesTestStatus } = splitDeviceTypesForApi(props);
  const roles = get(props, 'roles.value');
  const status = get(props, 'status.value');
  const supervisors = get(props, 'supervisors.value');
  const { customProperties } = props;
  const groups = get(props, 'groups.value');
  const sites = get(props, 'sites.value');
  const absent = get(props, 'absent.value');
  const ids = get(props, 'ids');
  const licenseType = get(props, 'licenseType.value');
  const dateCreated = get(props, 'dateCreated.value');

  const params = {};

  if (ids) {
    params.id = ids;
    // do not want apply any filters parameters  when calling with ids
    return params;
  }

  if (!isEmpty(dateCreated)) {
    const [
      {
        isDateRange,
        dateRangeFilterFrom,
        dateRangeFilterTo,
        daysToSubtract,
        loggedInUserTimezone,
      },
    ] = dateCreated;

    if (isDateRange) {
      params.createdFrom = moment(dateRangeFilterFrom)
        .tz(loggedInUserTimezone)
        .startOf('day')
        .toISOString();

      params.createdTo = moment(dateRangeFilterTo)
        .tz(loggedInUserTimezone)
        .endOf('day')
        .toISOString();
    } else {
      const fromDate = moment()
        .tz(loggedInUserTimezone)
        .subtract(daysToSubtract, 'days');

      // COR-29835
      // If `daysToSubtract` is 1 (ie: 24 hours), AC 2.b requires us to
      // search 24hrs exactly, hours/mins/secs inclusive. If greater
      // than 1, we'll round the start time down to begin at midnight
      // on the nth day, hence `.startOf('day')`.
      params.createdFrom =
        daysToSubtract === 1
          ? fromDate.toISOString()
          : fromDate.startOf('day').toISOString();
    }
  }

  if (devicesStatus) {
    params['devices.status'] = filterIfHasValue(devicesStatus);
  }

  if (devicesTestStatus) {
    params['devices.testStatus'] = filterIfHasValue(devicesTestStatus);
  }

  if (devicesExists && devicesExists.length === 1) {
    if (devicesExists[0] === 'USER_FILTER_DEVICES_EXISTS') {
      params['devices.exists'] = ['true'];
    } else if (devicesExists[0] === 'USER_FILTER_DEVICES_NONE_EXISTS') {
      params['devices.exists'] = ['false'];
    } else if (devicesExists[0] === 'USER_FILTER_DEVICES_EMAIL_NONE_EXISTS') {
      params['devices.email.exists'] = ['false'];
    } else if (
      devicesExists[0] === 'USER_FILTER_DEVICES_FAILSAFE_NONE_EXISTS'
    ) {
      params['devices.failsafe.exists'] = ['false'];
    } else if (devicesExists[0] === 'USER_FILTER_DEVICES_MOBILE_NONE_EXISTS') {
      params['devices.mobile.exists'] = ['false'];
    } else if (devicesExists[0] === 'USER_FILTER_DEVICES_SMS_NONE_EXISTS') {
      params['devices.sms.exists'] = ['false'];
    } else if (devicesExists[0] === 'USER_FILTER_DEVICES_VOICE_NONE_EXISTS') {
      params['devices.voice.exists'] = ['false'];
    }
  }

  if (roles) {
    params.roles = filterIfHasValue(roles);
  }

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = encodeURIComponent(trimmedSearchText);
    params.operand = 'AND';
    params.fields = ['FIRST_NAME', 'LAST_NAME', 'TARGET_NAME'];
  }

  if (supervisors && supervisors.length) {
    const { supervisorsId, supervisorsExists } =
      getSupervisorsValue(supervisors);
    params.supervisors = supervisorsId;
    params['supervisors.exists'] = supervisorsExists;
  }

  if (status && status.length === 1) {
    params.status = status;
  }

  if (!isEmpty(sites)) {
    const [siteId] = getSitesValue(sites);
    params.site = siteId;
  }

  if (!isEmpty(groups)) {
    const { groupNames, groupExists } = getGroupsValue(groups);
    if (!isEmpty(groupNames)) {
      // TODO: remove `|| g` after some time (exists in case people have Strings saved in session storage):
      params.groups = groupNames.map(g => g.id || g).join(',');
    }
    if (groupExists !== undefined) {
      params['groups.exists'] = groupExists;
    }
  }

  if (absent && absent.length === 1) {
    params.activeAbsences = absent;
  }

  if (customProperties) {
    const propertyNames = Object.keys(customProperties).filter(
      key => !isEmpty(customProperties[key].value),
    );

    if (propertyNames.length > 0) {
      // use string utility to encode special characters
      const escapeCustomValue = s =>
        s && encodeURIComponent(s).replace(/\*/g, '\\*');
      // escape internal commas on keys and values to ensure their lengths match:
      params.propertyNames = propertyNames
        .map(s => s && encodeReduxFieldName(s.toString()))
        .join(',');

      const getValue = key => {
        const { propertyType, value: customValue } = customProperties[key];
        const escapedValue = escapeCustomValue(customValue.toString());
        // Wrap STRING values in `*` to treat as a "CONTAINS" search via `allowWildcards=true`:
        return propertyType === 'STRING' ? `*${escapedValue}*` : escapedValue;
      };

      params.propertyValues = propertyNames.map(getValue).join(',');
      params.allowWildcards = true;
    }
  }

  if (licenseType && licenseType.length === 1) {
    params.licenseType = licenseType;
  }

  return params;
};

export const searchUsers = (props = {}) => {
  const { startIndex = 0, stopIndex = 0, sortBy, sortOrder, timeStamp } = props;

  const filterProps = deserializeCustomProps(props);

  const params = {
    limit: Math.max(100, stopIndex - startIndex),
    offset: startIndex,
    embed: 'roles,permittedActions',
    ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
    ...(sortBy && { sortBy: sortBy.toUpperCase() }),
    ...getSearchFilterParams(filterProps),
  };

  return {
    type: SEARCH_USERS_LIST_PAGE,
    meta: {
      xmApi: true,
      method: 'get',
      uri,
      params,
      isUriEncoded: true,
      customUriWithParamsGenerator: getListingURIGenerator,
    },
    payload: { timeStamp },
  };
};

export const USERS_LIST_PAGE_UPDATE_SEARCH_PARAMS =
  'USERS_LIST_PAGE_UPDATE_SEARCH_PARAMS';
export const updateUserDefinedFilterValues = payload => ({
  type: USERS_LIST_PAGE_UPDATE_SEARCH_PARAMS,
  payload,
});

export const USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS =
  'USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS';
export const USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS_START = `${USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS}_START`;
export const USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS_SUCCESS = `${USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS}_SUCCESS`;
export const USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS_FAIL = `${USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS}_FAIL`;

export const hydrateActiveDynamicFilters = payload => {
  return {
    type: USERS_LIST_PAGE_HYDRATE_ACTIVE_DYNAMIC_FILTERS,
    payload,
  };
};

export const USERS_LIST_PAGE_SEARCH_FILTER_ITEMS =
  'USERS_LIST_PAGE_SEARCH_FILTER_ITEMS';
export const USERS_LIST_PAGE_SEARCH_FILTER_ITEMS_START = `${USERS_LIST_PAGE_SEARCH_FILTER_ITEMS}_START`;
export const USERS_LIST_PAGE_SEARCH_FILTER_ITEMS_SUCCESS = `${USERS_LIST_PAGE_SEARCH_FILTER_ITEMS}_SUCCESS`;
export const USERS_LIST_PAGE_SEARCH_FILTER_ITEMS_FAIL = `${USERS_LIST_PAGE_SEARCH_FILTER_ITEMS}_FAIL`;

export const getFilterItemsBySearch = (endpoint, searchTerm) => dispatch => {
  const filterUri = `/webapi/xm/1/${endpoint}`;

  const params = {
    operand: 'AND',
    fields: 'NAME',
    search: searchTerm,
  };

  return dispatch({
    type: USERS_LIST_PAGE_SEARCH_FILTER_ITEMS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: filterUri,
      params,
    },
    payload: { endpoint },
  })
    .then(({ payload }) =>
      dispatch({
        type: USERS_LIST_PAGE_SEARCH_FILTER_ITEMS_SUCCESS,
        payload,
      }),
    )
    .catch(error =>
      dispatch({
        type: USERS_LIST_PAGE_SEARCH_FILTER_ITEMS_FAIL,
        payload: { endpoint, error },
      }),
    );
};

export const USERS_LIST_PAGE_RESET_DYNAMIC_FILTERS =
  'USERS_LIST_PAGE_RESET_DYNAMIC_FILTERS';
export const USERS_LIST_PAGE_SET_ACTIVE_DYNAMIC_FILTERS =
  'USERS_LIST_PAGE_SET_ACTIVE_DYNAMIC_FILTERS';
export const setActiveDynamicFilters = (key, items) => ({
  type: USERS_LIST_PAGE_SET_ACTIVE_DYNAMIC_FILTERS,
  payload: { key, items },
});
export const resetDynamicFilters = (key, items) => ({
  type: USERS_LIST_PAGE_RESET_DYNAMIC_FILTERS,
  payload: { key, items },
});

export const USERS_LIST_PAGE_SET_LOADING = 'USERS_LIST_PAGE_SET_LOADING';
export const setIsLoading = () => ({ type: USERS_LIST_PAGE_SET_LOADING });

const USERS_LIST_PAGE_DELETE_USER = 'USERS_LIST_PAGE_DELETE_USER';
export const deleteUser = userId => ({
  type: USERS_LIST_PAGE_DELETE_USER,
  meta: {
    xmApi: true,
    method: 'del',
    uri: `${uri}/${userId}`,
  },
});

export const USERS_LIST_PAGE_DELETE_USERS = 'USERS_LIST_PAGE_DELETE_USERS';
export const USERS_LIST_PAGE_DELETE_USERS_START = `${USERS_LIST_PAGE_DELETE_USERS}_START`;
export const USERS_LIST_PAGE_DELETE_USERS_SUCCESS = `${USERS_LIST_PAGE_DELETE_USERS}_SUCCESS`;
export const USERS_LIST_PAGE_DELETE_USERS_FAIL = `${USERS_LIST_PAGE_DELETE_USERS}_FAIL`;
export const deleteUsers = userIds => dispatch => {
  const onSuccess = () =>
    dispatch({ type: USERS_LIST_PAGE_DELETE_USERS_SUCCESS });
  const onFail = e => {
    dispatch({ type: USERS_LIST_PAGE_DELETE_USERS_FAIL });
    throw e;
  };

  const handleNext = () =>
    dispatch(deleteUser(userIds.pop()))
      .then(() => (userIds.length ? handleNext() : onSuccess()))
      .catch(onFail);

  dispatch({ type: USERS_LIST_PAGE_DELETE_USERS_START });
  return handleNext();
};

export const USERS_LIST_PAGE_EXPORT_USERS = 'USERS_LIST_PAGE_EXPORT_USERS';
export const USERS_LIST_PAGE_EXPORT_USERS_START = `${USERS_LIST_PAGE_EXPORT_USERS}_START`;
export const USERS_LIST_PAGE_EXPORT_USERS_SUCCESS = `${USERS_LIST_PAGE_EXPORT_USERS}_SUCCESS`;
export const USERS_LIST_PAGE_EXPORT_USERS_FAIL = `${USERS_LIST_PAGE_EXPORT_USERS}_FAIL`;
export const USERS_LIST_PAGE_EXPORT_USERS_FINISHED = `${USERS_LIST_PAGE_EXPORT_USERS}_FINISHED`;

export const exportUsers = ({ filters }) => {
  const filterProps = deserializeCustomProps(filters);
  const params = {
    embed: 'roles,devices,supervisors,passwordStatus,permittedActions',
    limit: 1000,
    ...getSearchFilterParams(filterProps),
  };

  return {
    type: USERS_LIST_PAGE_EXPORT_USERS,
    meta: {
      xmApi: true,
      method: 'getall',
      uri,
      params,
      customUriWithParamsGenerator: getListingURIGenerator,
      isUriEncoded: true,
      retries: 5,
    },
  };
};

export const exportUsersFinished = () => ({
  type: USERS_LIST_PAGE_EXPORT_USERS_FINISHED,
});
